// homeAiSend(text) — entry point for the home page's CanvasPromptTextarea.
//
// Replaces the direct `freestyleSend(text)` call (which always opens a
// local WebSocket to ws://127.0.0.1:8765 regardless of the user's
// provider mode). Routes through sdk.ai.dispatch so:
//
//   - mode='simone' / 'providers' → POST /core/ai-chat/dispatch (or
//     fallback to /ai-chat/stream until the dispatch endpoint ships)
//   - mode='local'                → WebSocket to simone-bridge through
//                                   the SDK's AiService instead of the
//                                   workspace's freestyleBridge module
//
// Intent routing (the LLM picks one of three):
//
//   - 'build'   → write the spec to root.freestyleBuildSpec and toggle
//                 root.freestyleBusy so the canvas / page-builder picks
//                 it up. (Today this is a stub — the actual freestyler
//                 hand-off lands when the server endpoint emits the
//                 spec shape; until then BUILD falls back to ANSWER.)
//   - 'answer'  → open AppAssistant drawer + push the streamed text as
//                 the latest assistant turn.
//   - 'action'  → write proposals to root._aiActionProposals so the
//                 shell-mounted AiActionConfirmCard renders. In
//                 authMode='auto' the SDK auto-executes; we write the
//                 resolved results to _aiActionResults instead.
//
// Per-intent state writes — see workspace/state-app.js for declarations.

// `attachments` is the optional image-bytes payload built by the shared
// buildPromptPayload serialiser (shared/functions/promptAttachments.js). It is
// a REAL second parameter, not the retry counter — the counter moved to the
// third slot when attachments were added, because the composer now calls
// homeAiSend(body, attachments) and passing an array where `_attempt` used to
// sit would have silently corrupted the boot-race retry below.
//
// Note the honest limit: the description of every attachment is already inside
// `text` (buildPromptPayload appends context lines), which is what actually
// reaches the agent today. Threading the bytes further is blocked downstream —
// see promptAttachments.js's header.
// `attached` is the OPTIONAL structured-attachment payload the canvas
// composer forwards from `getSelectionAiContext().attached`
// (`{ refs?, canvas? }`, spec §3.8/§4.1). It rides in the FOURTH slot, after
// the boot-race counter, on purpose: `_attempt` has been the third parameter
// since attachments were added, the retry below re-calls this function
// positionally, and moving it again is the same silent corruption the
// `attachments` note above records. Passed through UNTOUCHED — the canvas lane
// owns the payload's own caps, and simoneAiTransport merges it with the
// persistent `root.aiAttached` bag at the one place that builds the wire.
// `opts` (fifth slot, after `attached` for the same positional reason) —
// `{ mode }`: the ONE ask's output when the composer's focus panel names it
// (functions/aiPanel.js — "Summarize" answers, "Build a board" builds). It
// routes and rides this send only; the person's mode is never written
// (functions/assistantMode.js: an explicit choice wins over every default).
export async function homeAiSend(
  text,
  attachments = null,
  _attempt = 0,
  attached = null,
  opts = null
) {
  const el = this
  const root = el?.state?.root || el?.getRoot?.()?.state?.root

  const trimmed = String(text || '').trim()
  if (!trimmed) return
  const modes = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly']
  // INSPECT & ASK: an ask that carries a region the member pointed at (a
  // picked entry with `inspect` facts, functions/aiInspect.js) is a QUESTION
  // about it. It is ANSWERED — the chat agent reads the region's facts
  // (`context.inspected`) — never turned into a board or a deck by the
  // person's Dashboard / Presentation mode: on dev 2026-10-04 "why is this
  // amber?" built a generic board that never saw the region
  // (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1). One ask only; the
  // person's mode is never written.
  // An element picked in a WEBSITE's frame (a Fly site card's Inspect — the
  // preview picker's sanitized pick, `untrusted: true`) is the same kind of
  // pointing: the ask is about that element (Nika 2026-10-18, "inspect modes"
  // in Fly; preview functions/_simone/pickElement.js).
  // FLY keeps a REGION of the workspace in Fly (Fly train 2): the page
  // answers about it — its facts ride the fly ask as `context.inspected`
  // (functions/assistantFlyContext.js; the server's fly INSPECTED line, its
  // figures ON SCREEN facts of the opening). A website element still goes to
  // the chat, as the sites lane routes it.
  const picked = Array.isArray(root?.pickedElements) ? root.pickedElements : []
  const fly = root?.assistantMode === 'fly'
  const asksAboutRegion =
    picked.some((p) => p && (p.untrusted === true || (p.inspect && !fly))) &&
    (root?.assistantMode === 'dashboard' || root?.assistantMode === 'presentation' || fly)
  const askMode =
    opts && modes.includes(opts.mode) ? opts.mode : asksAboutRegion ? 'assistant' : null
  const effectiveMode = askMode || root?.assistantMode
  const fns = (el && el.context && el.context.functions) || {}
  // THE ASK ROUTER (functions/assistantAskRoute.js): ONE decision per ask —
  // the view is the default, a clear intent switches (a dashboard → a Fly
  // page, an app → the builder, slides → a deck, a document, an open or an
  // action → the chat, the tour → the tour), and a switch says so. An
  // explicit ask mode (the focus panel) and a question about a picked region
  // keep the routes below, and so does a host without the router.
  let decision =
    !askMode && root && typeof fns.assistantAskRoute === 'function'
      ? el.call('assistantAskRoute', root, trimmed)
      : null
  // A page or slides need the board service's answer (`_aiBoardsAvailable`):
  // asked on EVERY ask that needs it while it is not a yes — a service that
  // came back since the last ask is used at once — then decided again.
  if (decision && decision.probe) {
    try {
      await el.call('assistantBoardProbe', root, el.sdk || el.call('getSDK'))
    } catch (_) {
      /* no service here — the chat answers, and says so */
    }
    decision = el.call('assistantAskRoute', root, trimmed, { probed: true })
  }
  // An explicit app build belongs to the builder even when Ask, Fly or
  // Present is selected. Dashboard keeps its board-generation meaning.
  const buildApp = decision
    ? decision.surface === 'app'
    : effectiveMode !== 'dashboard' &&
      /^(?:(?:please|can you|could you|would you)\s+)*(?:build|make|create)\b[\s\S]*\b(?:app|application|extension|plugin|mini-app|board|tracker|portal)\b/i.test(
        trimmed
      )

  // MODE × PLACEMENT (AI_ASSISTANT_SPEC v1 §1, v1.1 §12; DESIGN-WL-ASSISTANT-
  // MAIN-VIEW-1 — the routing hunk of this file, cleared to current-23 by ws-w2
  // 2026-09-28). With the main placement (`assistantDock === 'main'`) on ANY
  // route (§12.2, main thread = "Everywhere"), a DASHBOARD ask builds a
  // generated board in the main area — `assistantBoardGenerate`, the
  // ai.boards stream, the drawer shut — while an ASSISTANT, TOOLS or APPS ask
  // becomes the MAIN THREAD: the same general-scope turn the sidebar runs,
  // over the home's grid area or the page (functions/assistantMainThread.js
  // `assistantSendRoute`). Another placement, a page sheet open in the
  // PageSlot, or a shell without that function takes the path below, as
  // today. The board route runs ahead of the transport readiness wait on
  // purpose: a board never touches the chat bridge.
  // PRESENTATION (§12.1) is the same path's second shape: a LIVE DECK
  // (functions/assistantDeck.js `assistantDeckGenerate`) — one fast call, no
  // tools, never the chat bridge; the deck takes the view like a board.
  let route = decision
    ? decision.route
    : buildApp
      ? 'app'
      : root && typeof fns.assistantSendRoute === 'function'
        ? el.call('assistantSendRoute', root, askMode)
        : null
  // A BOARD or a DECK needs the board service's answer (`_aiBoardsAvailable`),
  // which a board view asks when it MOUNTS — the home's. On a page where none
  // has mounted yet (a cold boot on /crm, §12.2), a Dashboard or Presentation
  // ask asks it first — once per workspace for this composer — then routes.
  // FLY needs the same answer on ANY placement (its canvas is the main area
  // whatever the dock): a fly ask with no route yet asks the probe first too.
  // Asked again on every such ask while the answer is not a yes (it was once
  // per composer: a cold boot's "no" stuck for the whole session).
  if (
    !decision &&
    (route === 'thread' || route === null) &&
    root &&
    (effectiveMode === 'fly' ||
      (route === 'thread' &&
        root.assistantDock === 'main' &&
        (effectiveMode === 'dashboard' || effectiveMode === 'presentation'))) &&
    root._aiBoardsAvailable !== true &&
    typeof fns.assistantBoardProbe === 'function'
  ) {
    try {
      await el.call('assistantBoardProbe', root, el.sdk || el.call('getSDK'))
    } catch (_) {
      /* no service here — the ask stays a thread turn */
    }
    route = el.call('assistantSendRoute', root, askMode)
  }
  // THE TOUR (functions/aiStartTour.js) from any view — never a page about
  // it. A tour that cannot start here leaves the ask to the chat.
  if (route === 'tour') {
    let tour = null
    try {
      tour = await el.call('aiStartTour')
    } catch (_) {
      tour = null
    }
    if (tour && tour.ok) return tour
    const pane = !!(root._canvasSlot || root._canvasSlotSecond)
    route = !pane && root.assistantDock === 'main' ? 'thread' : null
    decision = { ...decision, surface: route ? 'thread' : 'drawer', route, switched: false }
  }
  // A WEBSITE goes to the site builder the sites lane registers
  // (functions/assistantOpenSiteBuilder.js: the text, then the image bag and
  // the turn's attachment).
  if (route === 'site') {
    el.call('assistantAskNotify', decision)
    return el.call('assistantOpenSiteBuilder', trimmed, { images: attachments, attached })
  }
  // FLY (docs/FLY_MODE.md): the ask becomes the next page of the session —
  // never the chat bridge (one fast call; a board on a server without the
  // fly shape).
  if (route === 'fly') {
    // In a call with Fly on, on the call: the ask is the CALL's — its page
    // goes to everyone's theater (meet functions/meetFly.js).
    // An ask with files stays the person's own (a call's page carries no
    // attachments).
    const hasFiles =
      (Array.isArray(attachments) && attachments.length > 0) ||
      (Array.isArray(attached) && attached.length > 0)
    if (
      !hasFiles &&
      fns &&
      typeof fns.meetFlyComposerShared === 'function' &&
      el.call('meetFlyComposerShared', root)
    ) {
      return el.call('meetFlyAsk', { prompt: trimmed })
    }
    // FLY CALLS (Nika 2026-10-23: "add calling mode into fly so you can call
    // to anyone in workspace"): an ask that names ONE member — "call Nika" —
    // calls them (functions/assistantFlyPeople.js); any other ask composes
    // its page.
    if (!hasFiles && fns && typeof fns.assistantFlyCallIntent === 'function') {
      const callee = el.call('assistantFlyCallIntent', root, trimmed)
      if (callee) return el.call('assistantFlyCallPerson', callee)
    }
    if (decision) el.call('assistantAskNotify', decision)
    return el.call('assistantFlyGenerate', { prompt: trimmed, attachments, attached })
  }
  if (route === 'board' || route === 'deck') {
    // In a call with Fly on, on the call: a Dashboard or Presentation ask is
    // the CALL's too — its board (as a page) or its slides go to everyone's
    // theater, like a Fly page (Nika 2026-10-18, "main mode is theater in
    // different modes"). An ask with files stays the person's own.
    const views = el.context && el.context.functions
    const withFiles =
      (Array.isArray(attachments) && attachments.length > 0) ||
      (Array.isArray(attached) && attached.length > 0)
    if (
      !withFiles &&
      views &&
      typeof views.meetFlyComposerShared === 'function' &&
      el.call('meetFlyComposerShared', root)
    ) {
      return el.call('meetFlyAsk', { prompt: trimmed, kind: route })
    }
    try {
      el.call('assistantMainThreadClose')
    } catch (_) {
      /* no thread view on this surface — nothing to step aside */
    }
    // A call's theater steps aside too (one view holds the main layer).
    if (views && typeof views.meetFlyTheater === 'function') el.call('meetFlyTheater', false)
    if (decision) el.call('assistantAskNotify', decision)
    return el.call(route === 'deck' ? 'assistantDeckGenerate' : 'assistantBoardGenerate', {
      prompt: trimmed,
      attachments,
      attached
    })
  }

  // Boot race — a prompt sent in the first seconds after load could hit a
  // root that isn't wired yet (or a transport that hasn't installed) and was
  // SILENTLY DROPPED: the composer cleared, nothing happened (verified live
  // 2026-07-24 on /w/bellforge — the first prompt after reload vanished).
  // User input is never disposable: retry briefly until the shell is ready
  // instead of eating the message. ~8 × 500ms ≈ 4s covers the boot window.
  const ready =
    root && typeof root.update === 'function' && typeof window !== 'undefined'
      ? typeof window.__simoneSendOverBridge === 'function'
      : false
  if (!ready) {
    if (_attempt < 8) {
      setTimeout(() => {
        try {
          homeAiSend.call(el, trimmed, attachments, _attempt + 1, attached, opts)
        } catch (_) {
          /* element torn down — nothing to retry against */
        }
      }, 500)
    } else {
      console.warn('[homeAiSend] dropped prompt — shell/transport never became ready:', trimmed)
    }
    return
  }

  if (buildApp) {
    // ON THE HOME the Build tab IS the builder (functions/homeBuild.js): the
    // home's view moves there first — the tabs' local form, nothing persists —
    // so the build shows from its first line. Only with the main placement (a
    // tab would move the placement too).
    if (
      decision &&
      root.assistantDock === 'main' &&
      typeof fns.homeTabsOnScreen === 'function' &&
      typeof fns.homeViewSelect === 'function' &&
      el.call('homeTabsOnScreen', root)
    ) {
      el.call('homeViewSelect', 'build')
    }
    if (decision) el.call('assistantAskNotify', decision)
    return el.call('forceBuildApp', trimmed, attachments, attached)
  }
  // The ask's own mode for a chat turn: the panel's, or the router's (a turn
  // that leaves a page, a board or a deck view answers as the Assistant).
  const turnMode = decision ? decision.mode : askMode

  // THE MAIN THREAD: the turn renders in the MainColumn, never the drawer —
  // the thread view is up before the user message lands, so the reasoning
  // pill and the suggestion island step aside for it (`_homeThreadOpen`).
  if (route === 'thread') {
    try {
      el.call('assistantMainThreadShow')
      if (decision) el.call('assistantAskNotify', decision)
      el.call(
        'sendPrompt',
        trimmed,
        undefined,
        undefined,
        attached,
        turnMode ? { mode: turnMode } : undefined
      )
    } catch (err) {
      console.warn('[homeAiSend] main-thread send failed:', err?.message || err)
    }
    return
  }

  // Open the AssistantDrawer + dispatch through simone's sendPrompt.
  // That function builds the user message, snapshots it into
  // root.chatMessages, flips chatTyping:true, and calls
  // window.__simoneSendOverBridge — which workspace's
  // installSimoneAiTransport adapter routes to sdk.ai.dispatch with the
  // active provider mode (simone / providers / local) + intent
  // classification. The transport adapter also handles the action /
  // build / answer intent fan-out (writing _aiActionProposals for the
  // confirmation card, freestyleBuildSpec for the freestyler) so this
  // function stays a thin entry point.
  //
  // The legacy `assistantMessages` slot stays declared in state for
  // back-compat but is no longer written here — simone Chat reads
  // chatMessages exclusively.
  // The docked AssistantDrawer (left / right / minimized) is the single AI
  // surface on EVERY route including home (per Nika 2026-07-19 — the inline
  // dashboard thread is retired). A page open in the PageSlot suppresses the
  // drawer so the compact SimoneReasoningPill surfaces the turn above the
  // composer, over the page; otherwise the docked drawer opens (or, when the
  // user left it minimized, the pill shows the turn). Either way the turn runs
  // in the general scope, so the drawer's Chat + the pill both surface it. Clear
  // any lingering inline-thread flag so the retired thread can't re-appear.
  try {
    const pageSlotOpen = !!(root._canvasSlot || root._canvasSlotSecond)
    // ...but never force-CLOSE a drawer the user already has open — sending
    // should append to it, not collapse it mid-conversation. Only pageSlot-
    // suppress the OPEN when the drawer isn't already showing.
    const suppressed = pageSlotOpen && !root.assistantOpen
    // When the drawer IS suppressed the pill is the turn's only surface, so it
    // has to become a PERSISTENT handle for it. The pill's own visibility rule
    // is "show while the turn is live, or while the assistant is open+minimized"
    // — neither held here, so the pill disappeared the moment the turn finished
    // and the message the user had just sent (plus the answer) became
    // unreachable: no thread entry, no error, nothing (natali QA 2026-07-27).
    // This flag is the third handle case, and it is what makes the suppression
    // an intentional SMALL surface rather than no surface at all.
    root.update({
      assistantOpen: root.assistantOpen || !pageSlotOpen,
      _assistantPillHandle: suppressed || root._assistantPillHandle,
      _homeThreadOpen: false
    })
    if (decision) el.call('assistantAskNotify', decision)
    // `undefined` for scope + images: this entry point has no picker and the
    // turn is general-scoped. Named positionally so `attached` lands in
    // sendPrompt's fourth slot (see its signature note); the ask's own mode
    // (opts above) rides the fifth.
    el.call(
      'sendPrompt',
      trimmed,
      undefined,
      undefined,
      attached,
      turnMode ? { mode: turnMode } : undefined
    )
  } catch (err) {
    console.warn('[homeAiSend] sendPrompt failed:', err?.message || err)
  }
}
